import React from 'react';
import { Link } from 'react-router-dom';
import { User, Store, ArrowLeft, Shield } from 'lucide-react';

const LoginPortal: React.FC = () => {
  return (
    <div className="min-h-screen bg-slate-950 flex flex-col justify-between relative overflow-hidden font-sans selection:bg-[#0c513f] selection:text-white">
      {/* Decorative blurred background blobs */}
      <div className="absolute top-[-20%] left-[-10%] w-[600px] h-[600px] rounded-full bg-[#0c513f]/20 blur-[120px] pointer-events-none animate-pulse duration-10000" />
      <div className="absolute bottom-[-20%] right-[-10%] w-[600px] h-[600px] rounded-full bg-emerald-950/20 blur-[120px] pointer-events-none animate-pulse duration-7000" />

      {/* Header / Brand */}
      <header className="w-full max-w-7xl mx-auto px-6 py-6 flex items-center z-10">
        <Link to="/" className="flex items-center space-x-3 group">
          <img
            src="/rita_logo_2-removebg-preview.png"
            alt="Rita Logo"
            className="h-12 w-auto object-contain transition-transform group-hover:scale-105"
          />
          <span className="text-xl font-bold text-white tracking-tight">
            Rita
          </span>
        </Link>
      </header>

      {/* Main Portal Section */}
      <main className="flex-1 flex items-center justify-center px-4 py-12 z-10">
        <div className="w-full max-w-4xl bg-slate-900/60 backdrop-blur-xl border border-slate-800 rounded-3xl p-8 md:p-12 shadow-2xl relative">
          
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="px-3 py-1 text-xs font-semibold text-emerald-400 bg-emerald-950/50 border border-emerald-900/50 rounded-full uppercase tracking-wider">
              Rita Unified Access
            </span>
            <h1 className="text-3xl md:text-4xl font-extrabold text-white mt-4 tracking-tight">
              Select Your Portal
            </h1>
            <p className="text-slate-400 mt-3 text-base md:text-lg">
              Welcome to Rita. Choose the appropriate login portal below to access your account dashboard and services.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Customer Portal Card */}
            <a
              href="https://app.rita.ng"
              className="group relative flex flex-col justify-between bg-slate-950/40 hover:bg-slate-950/80 border border-slate-850 hover:border-[#0c513f]/40 p-6 md:p-8 rounded-2xl transition-all duration-300 hover:shadow-[0_0_30px_rgba(12,81,63,0.15)] overflow-hidden"
            >
              {/* Card top border glow effect on hover */}
              <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-emerald-500 to-[#0c513f] opacity-0 group-hover:opacity-100 transition-opacity" />

              <div>
                <div className="w-12 h-12 bg-emerald-950/50 border border-emerald-900/50 text-[#0c513f] rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <User size={24} className="text-emerald-400" />
                </div>
                <h3 className="text-xl font-bold text-white group-hover:text-emerald-400 transition-colors">
                  Customer Portal
                </h3>
                <p className="text-slate-400 mt-3 text-sm leading-relaxed">
                  Log in to manage your digital wallet, search & discover businesses, perform QR codes payments, and check your transaction history.
                </p>
              </div>

              <div className="mt-8 flex items-center text-sm font-semibold text-emerald-400 group-hover:translate-x-2 transition-transform">
                <span>Access Customer Portal</span>
                <span className="ml-2">→</span>
              </div>
            </a>

            {/* Provider Portal Card */}
            <a
              href="https://provider.rita.ng"
              className="group relative flex flex-col justify-between bg-slate-950/40 hover:bg-slate-950/80 border border-slate-850 hover:border-[#0c513f]/40 p-6 md:p-8 rounded-2xl transition-all duration-300 hover:shadow-[0_0_30px_rgba(12,81,63,0.15)] overflow-hidden"
            >
              {/* Card top border glow effect on hover */}
              <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-emerald-500 to-[#0c513f] opacity-0 group-hover:opacity-100 transition-opacity" />

              <div>
                <div className="w-12 h-12 bg-[#0c513f]/20 border border-[#0c513f]/30 text-emerald-400 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Store size={24} className="text-emerald-400" />
                </div>
                <h3 className="text-xl font-bold text-white group-hover:text-emerald-400 transition-colors">
                  Service Provider Portal
                </h3>
                <p className="text-slate-400 mt-3 text-sm leading-relaxed">
                  Log in to configure your digital store, upload KYC documents, manage your field offerings, settle wallet payouts, and track business reports.
                </p>
              </div>

              <div className="mt-8 flex items-center text-sm font-semibold text-emerald-400 group-hover:translate-x-2 transition-transform">
                <span>Access Provider Portal</span>
                <span className="ml-2">→</span>
              </div>
            </a>
          </div>

          <div className="mt-12 pt-6 border-t border-slate-800/80 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm">
            <Link
              to="/"
              className="flex items-center text-slate-400 hover:text-white transition-colors"
            >
              <ArrowLeft size={16} className="mr-2" />
              Back to Home
            </Link>
            
            <Link
              to="/admin/login"
              className="flex items-center text-slate-500 hover:text-emerald-400 transition-colors bg-slate-950/30 px-4 py-2 rounded-full border border-slate-800"
            >
              <Shield size={14} className="mr-1.5" />
              Administrative Login
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="w-full text-center py-6 text-xs text-slate-600 z-10 border-t border-slate-900">
        <p>© {new Date().getFullYear()} Rita Platform. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default LoginPortal;
